// src/admincomponents/WebmasterFiles.js
import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/apiClient'; // 假设 apiClient 在同一位置
import styled from 'styled-components';
import { FaFolder, FaFile, FaDownload, FaSyncAlt, FaArrowLeft, FaExclamationCircle, FaCheckCircle, FaHdd, FaTimesCircle } from 'react-icons/fa';
import {
    Panel, PanelHeader, PanelTitle, StatusText, ErrorNote, IconButton, LinkButton,
    TableWrap, Table, Th, Td, Tr,
} from './adminStyles';
import { color, font } from '../../theme/tokens';

// --- 样式定义 ---
const StatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    letter-spacing: 0.1em;
    color: ${({ $on }) => ($on ? color.text : color.bloodHi)};
    svg.state { color: ${({ $on }) => ($on ? color.denpa : color.bloodHi)}; }
`;

const Breadcrumb = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding: 10px 14px;
    border: 1px solid ${color.line};
    background: rgba(7, 2, 5, 0.5);
    font-family: ${font.mono};
    font-size: 13px;
    color: ${color.text2};
    .sep { margin: 0 6px; color: ${color.text3}; }
    .hint { font-family: ${font.body}; letter-spacing: 0.12em; color: ${color.text3}; }
`;

const RootList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid ${color.line};
    li {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border-bottom: 1px solid ${color.line};
        &:last-child { border-bottom: none; }
        svg { color: ${color.text3}; }
    }
`;

const NameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    svg { flex-shrink: 0; color: ${({ $dir }) => ($dir ? color.bloodHi : color.text3)}; }
`;

const Progress = styled.div`
    position: relative;
    width: 88px;
    height: 20px;
    border: 1px solid ${color.line2};
    background: ${color.void};
    overflow: hidden;
    .bar { height: 100%; background: ${color.blood}; transition: width 0.2s ease-in-out; }
    .pct {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: ${font.mono};
        font-size: 11px;
        color: ${color.onBlood};
    }
`;

const DoneIcon = styled.span`
    display: inline-flex;
    color: ${({ $error }) => ($error ? color.bloodHi : color.text)};
`;

// --- 辅助函数 ---
const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};


function WebmasterFiles() {
    const [status, setStatus] = useState({ isConnected: false, checking: true });
    const [roots, setRoots] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [pathParts, setPathParts] = useState([]);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [downloadProgress, setDownloadProgress] = useState({});

    const checkStatus = useCallback(async () => {
        setStatus(prev => ({ ...prev, checking: true }));
        try {
            const response = await apiClient.get('/admin/webmaster/status');
            setStatus({ isConnected: response.data.isConnected, checking: false });
            return response.data.isConnected;
        } catch (err) {
            setStatus({ isConnected: false, checking: false });
            return false;
        }
    }, []);

    const fetchData = useCallback(async (path) => {
        setIsLoading(true);
        setError('');
        try {
            if (!path) { // 获取根目录
                const response = await apiClient.get('/admin/webmaster/roots');
                setRoots(response.data);
                setFiles([]); // 清空文件，显示根目录
            } else { // 获取文件列表
                const response = await apiClient.get(`/admin/webmaster/files?path=${encodeURIComponent(path)}`);
                const sortedFiles = response.data.sort((a, b) => {
                    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
                    return a.name.localeCompare(b.name);
                });
                setFiles(sortedFiles);
            }
            setCurrentPath(path);
            setPathParts(path ? path.split(/[/]/) : []);
        } catch (err) {
            setError(err.response?.data?.message || '无法获取数据');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        checkStatus().then(connected => {
            if (connected) {
                fetchData(''); // 如果连接成功，获取根目录
            }
        });
    }, [checkStatus, fetchData]);
    
    const handleNavigation = (path) => {
        fetchData(path);
    };

    const handleBackClick = () => {
        if (pathParts.length <= 1) {
            handleNavigation(''); // 返回到根目录选择界面
        } else {
            const newPath = pathParts.slice(0, -1).join('/');
            handleNavigation(newPath);
        }
    };
    
    const handleBreadcrumbClick = (index) => {
        const newPath = pathParts.slice(0, index + 1).join('/');
        handleNavigation(newPath);
    };

    const handleDownloadClick = async (fileName) => {
        // 1. 防止重复点击
        if (downloadProgress[fileName]?.status === 'downloading') {
            console.log(`Download for ${fileName} is already in progress.`);
            return;
        }
 
        const fullPath = `${currentPath}/${fileName}`;
 
        // 2. 设置初始状态为 'downloading'
        setDownloadProgress(prev => ({
            ...prev,
            [fileName]: { progress: 0, status: 'downloading', error: null }
        }));
 
        try {
            const response = await apiClient.get(`/admin/webmaster/download?path=${encodeURIComponent(fullPath)}`, {
                responseType: 'blob',
                // 3. 关键：添加 onDownloadProgress 回调
                onDownloadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent;
                    if (total > 0) {
                        const percent = Math.floor((loaded * 100) / total);
                        setDownloadProgress(prev => ({
                            ...prev,
                            [fileName]: { ...prev[fileName], progress: percent }
                        }));
                    }
                }
            });
 
            // 下载成功
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
 
            // 4. 更新状态为 'completed'
            setDownloadProgress(prev => ({
                ...prev,
                [fileName]: { ...prev[fileName], status: 'completed' }
            }));
 
            // 5. (可选) 2秒后清除状态，让用户可以再次下载
            setTimeout(() => {
                setDownloadProgress(prev => {
                    const newState = { ...prev };
                    delete newState[fileName];
                    return newState;
                });
            }, 2000);
 
        } catch (err) {
            console.error(`Download failed:`, err);
            // 6. 更新状态为 'error'
            setDownloadProgress(prev => ({
                ...prev,
                [fileName]: { progress: 0, status: 'error', error: err.message }
            }));
            setError(`下载失败: ${err.message}`);
        }
    };
    

    const StatusIndicator = () => (
        <StatusRow $on={status.isConnected} role="status">
            {status.isConnected ?
                <><FaCheckCircle className="state" aria-hidden="true" /> <span>本地代理已连接</span></> :
                <><FaExclamationCircle className="state" aria-hidden="true" /> <span>本地代理未连接</span></>
            }
            <IconButton type="button" onClick={() => checkStatus().then(c => c && fetchData(currentPath))} disabled={status.checking} aria-label="重新检测连接" title="重新检测连接">
                <FaSyncAlt />
            </IconButton>
        </StatusRow>
    );

    const DownloadCell = ({ file }) => {
        const progressInfo = downloadProgress[file.name];

        if (progressInfo?.status === 'downloading') {
            return (
                <Progress role="progressbar" aria-valuenow={progressInfo.progress} aria-valuemin={0} aria-valuemax={100} aria-label={`下载 ${file.name}`}>
                    <div className="bar" style={{ width: `${progressInfo.progress}%` }}></div>
                    <span className="pct">{progressInfo.progress}%</span>
                </Progress>
            );
        }

        if (progressInfo?.status === 'completed') {
            return <DoneIcon title="下载完成"><FaCheckCircle aria-label="下载完成" /></DoneIcon>;
        }

        if (progressInfo?.status === 'error') {
            return <DoneIcon $error title={`错误: ${progressInfo.error}`}><FaTimesCircle aria-label="下载失败" /></DoneIcon>;
        }

        // 默认状态
        return (
            <IconButton
                type="button"
                onClick={() => handleDownloadClick(file.name)}
                disabled={progressInfo?.status === 'downloading'}
                title={`下载 ${file.name}`}
                aria-label={`下载 ${file.name}`}
            >
                <FaDownload />
            </IconButton>
        );
    };

    return (
        <Panel>
            <PanelHeader>
                <PanelTitle>站长本地文件</PanelTitle>
                <StatusIndicator />
            </PanelHeader>

            <section>
                {!status.isConnected && !status.checking && (
                    <ErrorNote role="alert">
                        无法连接到本地代理。请确保已在本地电脑正确运行代理程序。
                    </ErrorNote>
                )}

                {status.isConnected && (
                    <>
                        {/* 面包屑导航 */}
                        <Breadcrumb>
                            {pathParts.length > 0 && (
                                <IconButton type="button" onClick={handleBackClick} aria-label="返回上一级" title="返回上一级"><FaArrowLeft /></IconButton>
                            )}
                            <div>
                                {pathParts.length > 0 ? pathParts.map((part, index) => (
                                    <span key={index}>
                                        <LinkButton type="button" onClick={() => handleBreadcrumbClick(index)}>{part}</LinkButton>
                                        {index < pathParts.length - 1 && <span className="sep">/</span>}
                                    </span>
                                )) : <span className="hint">请选择一个根目录开始</span>}
                            </div>
                        </Breadcrumb>

                        {error && <ErrorNote role="alert">{error}</ErrorNote>}
                        {isLoading ? <StatusText>载入中…</StatusText> : (
                            !currentPath ? ( // 显示根目录
                                <RootList>
                                    {roots.map(root => (
                                        <li key={root.alias}>
                                            <FaHdd aria-hidden="true" />
                                            <LinkButton type="button" onClick={() => handleNavigation(root.path)}>
                                                {root.alias}
                                            </LinkButton>
                                        </li>
                                    ))}
                                </RootList>
                            ) : ( // 显示文件列表
                                <TableWrap>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <Th>名称</Th>
                                                <Th>大小</Th>
                                                <Th>修改日期</Th>
                                                <Th>操作</Th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {files.map(file => (
                                                <Tr key={file.name}>
                                                    <Td>
                                                        <NameCell $dir={file.isDirectory}>
                                                            {file.isDirectory
                                                                ? <><FaFolder aria-hidden="true" /> <LinkButton type="button" onClick={() => handleNavigation(`${currentPath}/${file.name}`)}>{file.name}</LinkButton></>
                                                                : <><FaFile aria-hidden="true" /> {file.name}</>
                                                            }
                                                        </NameCell>
                                                    </Td>
                                                    <Td $mono $dim>{file.isDirectory ? '-' : formatBytes(file.size)}</Td>
                                                    <Td $mono $dim>{new Date(file.lastModified).toLocaleString()}</Td>
                                                    <Td>
                                                        {!file.isDirectory && <DownloadCell file={file} />}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </TableWrap>
                            )
                        )}
                    </>
                )}
            </section>
        </Panel>
    );
}

export default WebmasterFiles;
